import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { Grid, Typography, Box, Alert } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { getData } from "../../Services/getData";
import { ExamQuestionModel } from "../../Utils/Models";
import { endpoints, localUrlEnum } from "../../Utils/UrlEnum";
import ExamHeader from "./ExamHeader";
import ExamExplanation from "./ExamExplanation";
import ExamImage from "./ExamImage";
import ExamAnswers from "./ExamAnswears";
import ExamFooter from "./ExamFooter";
import { isMobile } from "../../Utils/Utils";
import { postData } from "../../Services/postData";
import { disableTostify, examCategories } from "../../Utils/Constants";
import { Vocabulary } from "../../Utils/Vocabulary";
import QuestionSummary from "./QuestionSummary";
import Config from "../../Utils/Config";
import FinishedExamPage from "./FinishedExamPage";
import ExamAlert from "./ExamAlert";
import { deleteData } from "../../Services/deleteData";
import styles from "../../Styles/examPage.module.css";

export default function ExamPage() {
  const { examSetId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [questions, setQuestions] = useState<ExamQuestionModel[]>([]);
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const clientId = localStorage.getItem("clientId");

  // Get exam category from URL search params or localStorage
  const examCategoryFromUrl = searchParams.get("examCategory");
  const examCategoryFromStorage = localStorage.getItem("examCategory");
  const examCategory: string =
    examCategoryFromUrl ||
    (examCategoryFromStorage && examCategoryFromStorage !== "undefined"
      ? examCategoryFromStorage
      : "");

  const [showSummary, setShowSummary] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [areStatsCreated, setAreStatsCreated] = useState(false);

  // Get productId from URL params
  const productId = searchParams.get("productId");

  const examMode = !examSetId;
  const [showStartModal, setShowStartModal] = useState(examMode);
  const navigate = useNavigate();

  // Get max allowed wrong answers for the exam category
  const categoryData =
    examCategories[examCategory as keyof typeof examCategories];
  const maxAllowedWrong =
    categoryData?.maxAllowedWrong || Config.maxWrongExamQuestions;

  useEffect(() => {
    if (examMode) {
      setShowStartModal(true); // show modal only if no set is selected and only one category is available
    }
  }, []);

  // If all questions are answered, show a success message
  const allAnswered = questions.every((q) => q?.progress);

  // Fetch the questions from the backend
  useEffect(() => {
    const fetchQuestions = async () => {
      let data: any = null;

      // Check if practice questions were passed via state
      const practiceQuestions = (location.state as any)?.practiceQuestions;
      if (practiceQuestions && practiceQuestions.length > 0) {
        data = practiceQuestions;
      } else {
        let url = "";

        // If productId is provided, fetch from simulateByProduct endpoint
        if (productId) {
          url = `${endpoints.examQuestions.simulateByProduct}/${productId}`;
        }
        // Otherwise, use the existing category-based endpoint
        else {
          const categoryData =
            examCategories[examCategory as keyof typeof examCategories];
          const totalQuestions = categoryData?.totalQuestions || 26;
          url = `${endpoints.examQuestions.set}/${examSetId}/${clientId}/${
            examCategory == "" ? null : examCategory
          }/${totalQuestions}`;
        }

        if (url) {
          const res = await getData(url);
          data = res?.data;
        }
      }

      if (data) {
        setQuestions(data);
        // Find the first unanswered question
        const firstUnansweredIdx = data.findIndex(
          (q: ExamQuestionModel) => !q.progress
        );
        const allAnswered = data?.every((q: any) => q.progress);
        if (!allAnswered) {
          setCurrent(firstUnansweredIdx !== -1 ? firstUnansweredIdx : 0);
        } else {
          // If all answered, show the last question with its answer
          const lastQuestionIdx = data.length - 1;
          const lastQuestion = data[lastQuestionIdx];
          setCurrent(lastQuestionIdx);
          setSelected(lastQuestion.progress?.selectedAnswers || []);
          setShowResult(true);
          setIsCorrect(lastQuestion.progress?.isCorrect || false);
        }
      }
    };

    fetchQuestions();
  }, [examSetId, productId, clientId, examCategory, searchParams, location.state]);

  // If there are no questions, show a loading message
  if (!questions.length)
    return (
      <Typography align="center" mt={8}>
        Loading...
      </Typography>
    );

  if (showStartModal) {
    return (
      <ExamAlert
        open={showStartModal}
        onConfirm={() => setShowStartModal(false)}
        onCancel={() => {
          navigate(localUrlEnum.questionnaire);
        }}
      />
    );
  }

  // Count the number of correct and wrong answers
  // for the current question
  const correctCount = questions.filter(
    (q) => q.progress && q.progress.isCorrect
  ).length;
  const wrongCount = questions.filter(
    (q) => q.progress && q.progress.isCorrect === false
  ).length;

  /**
   * Creates statistics for the exam.
   * It posts the data to the /statistics endpoint.
   * The data sent is:
   * - clientId: the client id
   * - questionList: an array of objects with the _id property of each question
   * - passed: a boolean indicating if the exam was passed or not
   * - answers: an array of objects with the _id property of each question and the selected answer
   * - score: the number of correct answers
   * - totalQuestions: the total number of questions
   * - completedAt: the date and time when the exam was completed
   * @returns {Promise<void>}
   */
  const handleCreateStats = async () => {
    // Create statistics for the exam
    const questionList = questions.map((q) => ({ _id: q._id }));
    const answeredList = questions.map((q) => ({
      _id: q._id,
      isCorrect: q.progress?.isCorrect || false,
      selectedAnswer: q.progress?.selectedAnswers[0] || "",
    }));

    const body = {
      clientId,
      questionList,
      //TODO: Check if the exam is passed
      passed: wrongCount < maxAllowedWrong,
      answers: answeredList,
      score: correctCount,
      totalQuestions: questions.length,
      completedAt: new Date().toISOString(),
    };
    const url = `${endpoints.statistics}`;
    const res = (await postData(url + disableTostify, body)) as any;
    if (!res || res?.error) {
      return;
    }
  };

  if (isFinished && !areStatsCreated) {
    // If the exam is finished, create statistics
    handleCreateStats();
    setAreStatsCreated(true);
  }

  /**
   * If the exam is finished, show the finished page
   */
  if (isFinished && examMode) {
    // Handle the case when the exam is finished
    return (
      <FinishedExamPage
        correctAnswers={correctCount}
        wrongAnswers={wrongCount}
        examCategory={examCategory}
      />
    );
  }
  const handleQuestionaryReset = async () => {
    // 1 . Call Request to remove on backend progress
    const url = `${endpoints.examSets.main}/${examSetId}/${clientId}`;
    const res = (await deleteData(url)) as any;
    if (!res || res?.error) {
      return;
    }
    //2. On success navigate to UrlEnum.localUrlEnum.questionnaire;
    navigate(localUrlEnum.questionnaire);
  };

  if (allAnswered && !areStatsCreated) {
    // If the exam is finished, create statistics
    handleCreateStats();
    setAreStatsCreated(true);
  }

  // Get the current question
  const q = questions[current];

  /**
   * Toggles the selection state of an answer option by its index.
   * If the result is already shown, it prevents any selection changes.
   * Updates the selected options by adding or removing the specified index.
   * Clears any existing error state.
   *
   * @param idx - The index of the answer option to toggle.
   */

  const handleSelect = (idx: number) => {
    if (showResult) return;
    setSelected((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
    setError(null);
  };

  /**
   * Finds the index of the next unanswered question.
   * It starts from the current question index and increments until it finds a question with no progress.
   * If no such question is found, it returns the last question index.
   * @returns The index of the next unanswered question.
   */
  const findNextUnansweredQuestion = () => {
    let nextIdx = current + 1;
    while (nextIdx < questions.length && questions[nextIdx].progress) {
      nextIdx++;
    }
    return nextIdx;
  };

  const updateScore = (isAnswerCorrect: boolean) => {
    const updatedWrong = isAnswerCorrect ? wrongCount : wrongCount + 1;
    const allAnswered = current == questions.length - 1;

    // Get max allowed wrong answers for the current exam category
    const categoryData =
      examCategories[examCategory as keyof typeof examCategories];
    const maxAllowedWrong =
      categoryData?.maxAllowedWrong || Config.maxWrongExamQuestions;

    if (examMode && (updatedWrong > maxAllowedWrong || allAnswered)) {
      setIsFinished(true);
    }
  };

  /**
   * Handles the submission of an answer.
   * If no answer is selected, it shows an error.
   * Checks if the selected answers are correct and updates the score.
   * Submits the answer to the backend.
   */
  const handleSubmit = async () => {
    if (selected.length === 0) {
      setError(Vocabulary.selectAtLeastOneAnswer);
      return;
    }

    const correctIndexes = q.answers
      .map((a, i) => (a.isCorrect ? i : -1))
      .filter((i) => i !== -1);
    const isAllCorrect =
      selected.length === correctIndexes.length &&
      selected.every((i) => correctIndexes.includes(i));
    setIsCorrect(isAllCorrect);
    setShowResult(true);

    // submit the answear on the backend
    const body = {
      clientId: clientId,
      questionId: q._id,
      selectedAnswers: selected,
      isCorrect: isAllCorrect,
      examSetId: examSetId,
    };
    await postData(endpoints.clientProgress + disableTostify, body);

    //  Update the questions state with new progress
    setQuestions((prevQuestions) =>
      prevQuestions.map((question, index) =>
        index === current
          ? {
              ...question,
              progress: {
                selectedAnswers: selected,
                isCorrect: isAllCorrect,
              },
            }
          : question
      )
    );
    updateScore(isAllCorrect);
    if (examMode) handleNext();
  };

  /**
   * Advances to the next unanswered question in the exam.
   * Resets the current question's result state, including showResult, isCorrect, selected answers, and error.
   * If there are no more unanswered questions after the current one, it wraps around to the first unanswered question.
   */

  const handleNext = () => {
    setShowResult(false);
    setIsCorrect(null);
    setSelected([]);
    setError(null);

    // Find the next unanswered question
    const nextIdx = findNextUnansweredQuestion();

    // If no unanswered question found,  go back to first unanswered
    if (nextIdx < questions.length) {
      setCurrent(nextIdx);
    } else {
      // Go to the first unanswered
      const firstUnansweredIdx = questions.findIndex((q) => !q.progress);
      setCurrent(firstUnansweredIdx !== -1 ? firstUnansweredIdx : 0);
    }
  };

  /**
   * Renders the QuestionSummary component if showSummary is true.
   * Passes the required props to QuestionSummary.
   * When an item is selected, it updates the state of the selected question:
   * - If the question was already answered, it shows the previous answers and result.
   * - If the question is new, it resets the selected answers and result.
   * - It also updates the current question index.
   * - On mobile, it hides the summary after selecting an item.
   * @returns A JSX element containing the QuestionSummary component.
   */
  const renderQuestionsSummary = () => {
    return (
      showSummary && (
        <QuestionSummary
          questions={questions}
          onSelect={(index) => {
            const selectedQuestion = questions[index];

            // Show previous answers if already answered
            if (selectedQuestion.progress) {
              setSelected(selectedQuestion.progress.selectedAnswers || []);
              setShowResult(true);
              setIsCorrect(selectedQuestion.progress.isCorrect);
            } else {
              setSelected([]);
              setShowResult(false);
              setIsCorrect(null);
            }

            setError(null);
            setCurrent(index);

            if (isMobile()) setShowSummary(false);
          }}
          currentIndex={current}
        />
      )
    );
  };
  /**
   * Renders the ExamPage component.
   */
  return (
    <div
      className={
        isMobile() ? styles.examPageContainerMobile : styles.examPageContainer
      }
    >
      {renderQuestionsSummary()}
      <Box
        className={isMobile() ? styles.examContentMobile : styles.examContent}
      >
        <ExamHeader
          total={questions.length}
          remaining={questions.filter((q) => !q.progress).length} // <-- only unanswered
          correct={correctCount}
          wrong={wrongCount}
          handleClick={() => setShowSummary(!showSummary)}
          handleQuestionaryReset={handleQuestionaryReset}
          showSummary={showSummary}
          setIsFinished={setIsFinished}
          examCategory={examCategory}
        />

        {/* Completion Banner - Show when all questions are answered */}
        {allAnswered && !examMode && (
          <Box className={styles.examGridContainer}>
            <Alert
              severity="success"
              className={styles.modernCompletionAlert}
              icon={<CheckCircleOutlineIcon className={styles.alertIcon} />}
            >
              <Typography variant="h6" className={styles.alertTitle}>
                {Vocabulary.finishedTest}
              </Typography>
              <Typography variant="body2" className={styles.alertSubtext}>
                {Vocabulary.allQuestionsAnsweredMessage}
              </Typography>
            </Alert>
          </Box>
        )}

        {/* Individual Question Display */}
        <Grid container spacing={1} className={styles.examGridContainer}>
          {/* Question Text - Full width on all screens */}
          <Grid item xs={12} className={styles.examQuestionGrid}>
            <Box className={styles.examQuestion}>
              <Typography
                variant="h4"
                align="center"
                className={styles.questionTitle}
              >
                {q.questionText}
              </Typography>
            </Box>
          </Grid>

          {/* Image - Show on mobile/tablet above answers */}
          {isMobile() && q?.imageUrl && (
            <Grid item xs={12}>
              <ExamImage imageUrl={q?.imageUrl} />
            </Grid>
          )}

          {/* Answers Section */}
          <Grid
            item
            xs={12}
            lg={q?.imageUrl ? 6 : 12}
            className={styles.examAnswersGrid}
          >
            <ExamAnswers
              question={q}
              selected={selected}
              showResult={!examMode && showResult}
              handleSelect={handleSelect}
              error={error}
            />
          </Grid>

          {/* Image - Show on large desktop next to answers */}
          {!isMobile() && q?.imageUrl && (
            <Grid item xs={12} lg={6}>
              <ExamImage imageUrl={q?.imageUrl} />
            </Grid>
          )}
        </Grid>
        {/** show explanation only in learning mode */}
        {!examMode && <ExamExplanation isCorrect={isCorrect} question={q} />}

        <Box className={styles.examFooterContainer}>
          <ExamFooter
            showResult={showResult}
            handleNext={handleNext}
            handleSubmit={handleSubmit}
            setSelected={setSelected}
            allAnswered={allAnswered}
          />
        </Box>
      </Box>
    </div>
  );
}
